import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ReactComponent as TeamNameIcon } from '../../assets/icons/team-name-icon.svg';
import { ReactComponent as RightArrowIcon } from '../../assets/icons/arrow-right-icon.svg';
import { ReactComponent as DataIcon } from '../../assets/icons/data-icon.svg';
import logo from '../../assets/images/vairo-logo.png';
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen';
import { UserContext, OrgContext, WorkspaceContext } from '../../objects/Context';
import { clearStoredOAuthFlow, getStoredOAuthFlow } from '../../utils/oauthFlow';
import './OAuthConsent.css';

const OAuthConsent = () => {
    const { user } = useContext(UserContext);
    const { org } = useContext(OrgContext);
    const { workspace } = useContext(WorkspaceContext);
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [flowInfo, setFlowInfo] = useState(null);
    const [selectedWorkspaceId, setSelectedWorkspaceId] = useState(null);

    const flowId = searchParams.get('flow') || getStoredOAuthFlow();

    useEffect(() => {
        if (!flowId) {
            setError('No authorization session found. Please retry from your client.');
            setLoading(false);
            return;
        }
        fetchFlow();
    }, [flowId]);

    const fetchFlow = async () => {
        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_BASE_URL}/api/oauth/flow/${flowId}`,
                { withCredentials: true }
            );
            setFlowInfo(response.data);
            const workspaces = response.data.workspaces || [];
            // Auto-select the active workspace if it's accessible, else the only one.
            const active = workspaces.find(ws => ws.id === workspace?.id);
            if (active) {
                setSelectedWorkspaceId(active.id);
            } else if (workspaces.length === 1) {
                setSelectedWorkspaceId(workspaces[0].id);
            }
        } catch (err) {
            const errorCode = err.response?.data?.error;
            const description = err.response?.data?.error_description || errorCode;
            setError(description || 'Failed to load authorization session.');
            // Stale flow id (expired or invalid) — drop it so future logins
            // route to the normal app instead of getting stuck here.
            if (err.response?.status === 404 || errorCode === 'flow_not_found') {
                clearStoredOAuthFlow();
            }
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async () => {
        if (!selectedWorkspaceId) {
            setError('Please select a workspace to authorize.');
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/oauth/flow/${flowId}/approve`,
                { workspace_id: selectedWorkspaceId },
                { withCredentials: true }
            );
            clearStoredOAuthFlow();
            window.location.href = response.data.redirect_url;
        } catch (err) {
            const description = err.response?.data?.error_description || err.response?.data?.error;
            setError(description || 'Authorization failed.');
            setSubmitting(false);
        }
    };

    const handleDeny = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE_URL}/api/oauth/flow/${flowId}/deny`,
                {},
                { withCredentials: true }
            );
            clearStoredOAuthFlow();
            window.location.href = response.data.redirect_url;
        } catch (err) {
            const description = err.response?.data?.error_description || err.response?.data?.error;
            setError(description || 'Could not cancel authorization.');
            setSubmitting(false);
        }
    };

    if (loading) {
        return <LoadingScreen isLoading={loading} />;
    }

    const workspaces = flowInfo?.workspaces || [];
    const clientName = flowInfo?.client_name || 'this application';

    return (
        <div className="oauth-consent">
            <div className="content">
                <img src={logo} alt="Vairo" className="logo" />
                <h2>Authorize {clientName}</h2>
                <div className="dots">
                    <div className="dot" />
                    <div className="dot" />
                    <div className="dot" />
                </div>
                <p className="info-text">
                    <strong>{clientName}</strong> is requesting read-only access to your
                    financial data in Vairo.
                </p>

                {error && <p className="error-message">{error}</p>}

                {workspaces.length === 0 ? (
                    <>
                        <p className="info-text">
                            You don't have any workspaces yet. Create one before connecting.
                        </p>
                        <button
                            type="button"
                            className="submit-btn"
                            onClick={() => navigate('/org-setup')}
                        >
                            Set up workspace
                        </button>
                    </>
                ) : (
                    <>
                        <p className="info-text section-label">
                            {workspaces.length === 1 ? 'Workspace' : 'Choose a workspace'}
                        </p>
                        <ul className="ws-list">
                            {workspaces.map((ws) => {
                                const dsCount = ws.data_source_count ?? 0;
                                const dsLabel = dsCount === 0
                                    ? 'No data sources'
                                    : `${dsCount} data source${dsCount === 1 ? '' : 's'}`;
                                return (
                                    <li
                                        key={ws.id}
                                        className={`input-wrapper ws-item${selectedWorkspaceId === ws.id ? ' selected' : ''}`}
                                        onClick={() => setSelectedWorkspaceId(ws.id)}
                                    >
                                        <TeamNameIcon className="icon" />
                                        <div className="ws-text">
                                            <div className="ws-name">{ws.name}</div>
                                            <div className={`ws-meta${dsCount === 0 ? ' empty' : ''}`}>{dsLabel}</div>
                                        </div>
                                        {selectedWorkspaceId === ws.id && <RightArrowIcon className="icon" />}
                                    </li>
                                );
                            })}
                        </ul>

                        <div className="scope-row">
                            <DataIcon className="icon" />
                            <span>Read-only access to your connected data sources</span>
                        </div>

                        {selectedWorkspaceId && (workspaces.find(w => w.id === selectedWorkspaceId)?.data_source_count ?? 0) === 0 && (
                            <p className="info-text empty-sources-hint">
                                This workspace has no connected data sources yet. You can still authorize — once connected, ask {clientName} to "connect QuickBooks" and it will guide you through linking one.
                            </p>
                        )}

                        <button
                            type="button"
                            className="submit-btn"
                            onClick={handleApprove}
                            disabled={submitting || !selectedWorkspaceId}
                        >
                            {submitting ? 'Authorizing...' : 'Authorize'}
                        </button>
                        <button
                            type="button"
                            className="cancel-btn"
                            onClick={handleDeny}
                            disabled={submitting}
                        >
                            Cancel
                        </button>
                    </>
                )}

                <p className="support-text">
                    Signed in as <strong>{user?.email}</strong>
                    {org?.name ? <> · {org.name}</> : null}
                </p>
            </div>
        </div>
    );
};

export default OAuthConsent;
